import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Plus, X, Phone, PhoneCall, Search, Flame, Sun, Snowflake,
    CheckCircle2, RefreshCw, Trash2, Clock, IndianRupee,
} from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { supabase } from "../../utils/supabaseClient";
import { getCurrentUser } from "../../utils/session";
import { formatDate, formatDateTime, formatDuration, OUTCOME_LABELS, STATUS_LABELS } from "../../utils/crmHelpers";
import "../../styles/crm.css";

const EMPTY_LEAD = {
    lead_id: null, lead_name: "", place: "", contact_person: "",
    mobile_number: "", interested_software: [], source: "other",
    assigned_user_id: "", status: "lead", temperature: "warm",
};

const EMPTY_CALL = {
    outcome: "call_back_later", notes: "", next_call_date: "",
    temperature: "warm", demo_requested: false, demo_installed: false,
};

const SOURCE_OPTIONS = [
    { value: "cold_call", label: "Cold Call" },
    { value: "referral", label: "Referral" },
    { value: "partner", label: "Partner" },
    { value: "website", label: "Website" },
    { value: "exhibition", label: "Exhibition" },
    { value: "auto_install", label: "Auto Install" },
    { value: "other", label: "Other" },
];

const TEMP_ICON = { hot: Flame, warm: Sun, cold: Snowflake };

const TabFilters = [
    { key: "active", label: "Active" },
    { key: "confirmed", label: "Confirmed / Payment Pending" },
    { key: "client", label: "Converted Clients" },
    { key: "lost", label: "Lost" },
    { key: "all", label: "All" },
];

const CrmLeads = () => {
    const currentUser = getCurrentUser();

    const [search, setSearch] = useState("");
    const [tab, setTab] = useState("active");
    const [softwareSearch, setSoftwareSearch] = useState("");

    const [showAddForm, setShowAddForm] = useState(false);
    const [form, setForm] = useState(EMPTY_LEAD);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const [selectedLead, setSelectedLead] = useState(null);
    const [callForm, setCallForm] = useState(EMPTY_CALL);
    const [logging, setLogging] = useState(false);
    const [timerRunning, setTimerRunning] = useState(false);
    const [callSeconds, setCallSeconds] = useState(0);
    const timerRef = useRef(null);

    const [showConfirmForm, setShowConfirmForm] = useState(false);
    const [confirmForm, setConfirmForm] = useState({ amount: "", installments: 1, firstDueDate: "" });
    const [confirming, setConfirming] = useState(false);

    const {
        data: leads = [], loading, fetchAll: fetchLeads, save: saveLead, remove: deleteLead,
    } = useGeneric({ tableName: "crm_leads", idColumn: "lead_id", removeOnInsert: ["lead_id"] });

    const { data: executives = [], fetchAll: fetchExecutives } = useGeneric({
        tableName: "executives", idColumn: "executive_id", orderBy: "executive_name",
    });

    const { data: softwareOptions = [], fetchAll: fetchSoftware } = useGeneric({
        tableName: "productgroupmaster", idColumn: "group_id", orderBy: "group_name",
    });

    const {
        data: activities = [], fetchAll: fetchActivities, save: saveActivity,
    } = useGeneric({
        tableName: "crm_activities", idColumn: "activity_id", orderBy: "activity_date",
        filters: selectedLead ? { lead_id: selectedLead.lead_id } : undefined,
    });

    const {
        data: schedule = [], fetchAll: fetchSchedule, save: saveScheduleRow,
    } = useGeneric({
        tableName: "crm_payment_schedule", idColumn: "schedule_id", orderBy: "due_date",
        filters: selectedLead ? { lead_id: selectedLead.lead_id } : undefined,
    });

    useEffect(() => { fetchLeads(); fetchExecutives(); fetchSoftware(); }, [fetchLeads, fetchExecutives, fetchSoftware]);
    useEffect(() => {
        if (selectedLead) { fetchActivities(); fetchSchedule(); }
    }, [selectedLead, fetchActivities, fetchSchedule]);

    // Call timer
    useEffect(() => {
        if (timerRunning) {
            timerRef.current = setInterval(() => setCallSeconds((s) => s + 1), 1000);
        } else if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        return () => clearInterval(timerRef.current);
    }, [timerRunning]);

    const softwareName = (id) => softwareOptions.find((s) => Number(s.group_id) === Number(id))?.group_name ?? "—";
    const executiveName = (id) => executives.find((x) => Number(x.executive_id) === Number(id))?.executive_name ?? "—";

    // ── Filtering ────────────────────────────────────────────────
    const filteredLeads = useMemo(() => {
        let rows = leads;
        if (tab === "active") rows = rows.filter((l) => ["lead", "in_progress"].includes(l.status));
        else if (tab !== "all") rows = rows.filter((l) => l.status === tab);

        const q = search.trim().toLowerCase();
        if (q) {
            rows = rows.filter((l) =>
                (l.lead_name ?? "").toLowerCase().includes(q) ||
                (l.mobile_number ?? "").toLowerCase().includes(q) ||
                (l.place ?? "").toLowerCase().includes(q)
            );
        }
        const tempOrder = { hot: 0, warm: 1, cold: 2 };
        return rows.slice().sort((a, b) => {
            const dateA = a.next_call_date ? new Date(a.next_call_date).getTime() : Infinity;
            const dateB = b.next_call_date ? new Date(b.next_call_date).getTime() : Infinity;
            if (dateA !== dateB) return dateA - dateB;
            return (tempOrder[a.temperature] ?? 3) - (tempOrder[b.temperature] ?? 3);
        });
    }, [leads, tab, search]);

    // ── Add lead ─────────────────────────────────────────────────
    const set = (key, val) => { setForm((p) => ({ ...p, [key]: val })); setErrors((p) => ({ ...p, [key]: "" })); };

    const toggleSoftware = (id) => {
        setForm((p) => {
            const current = p.interested_software ?? [];
            const has = current.map(Number).includes(Number(id));
            return { ...p, interested_software: has ? current.filter((x) => Number(x) !== Number(id)) : [...current, id] };
        });
    };

    const validate = () => {
        const errs = {};
        if (!form.lead_name.trim()) errs.lead_name = "Name is required.";
        if (!form.mobile_number.trim()) errs.mobile_number = "Mobile number is required.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const openAdd = () => { setForm(EMPTY_LEAD); setErrors({}); setSoftwareSearch(""); setShowAddForm(true); };
    const closeAdd = () => { setShowAddForm(false); setForm(EMPTY_LEAD); setErrors({}); };

    const onSaveLead = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await saveLead({ ...form, created_by: currentUser?.executive_id ?? null });
            closeAdd();
        } catch (err) {
            alert("Could not save lead: " + (err.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    };

    const onDeleteLead = async (lead) => {
        if (!window.confirm(`Delete lead "${lead.lead_name}"? This cannot be undone.`)) return;
        try { await deleteLead(lead.lead_id); } catch (err) { alert("Delete failed: " + err.message); }
    };

    // ── Lead workspace ───────────────────────────────────────────
    const openLead = (lead) => {
        setSelectedLead(lead);
        setCallForm({ ...EMPTY_CALL, temperature: lead.temperature || "warm" });
        setCallSeconds(0);
        setTimerRunning(false);
    };
    const closeLead = () => {
        setTimerRunning(false);
        setSelectedLead(null);
        setShowConfirmForm(false);
    };

    const onLogCall = async () => {
        if (!selectedLead) return;
        setLogging(true);
        setTimerRunning(false);
        try {
            await saveActivity({
                lead_id: selectedLead.lead_id,
                user_id: currentUser?.executive_id ?? null,
                duration_seconds: callSeconds,
                outcome: callForm.outcome,
                notes: callForm.notes,
                next_call_date: callForm.next_call_date || null,
                temperature: callForm.temperature,
            });

            const updates = {
                ...selectedLead,
                status: selectedLead.status === "lead" ? "in_progress" : selectedLead.status,
                temperature: callForm.temperature,
                next_call_date: callForm.next_call_date || null,
            };
            if (callForm.demo_requested && !selectedLead.demo_requested_date) {
                updates.demo_requested_date = new Date().toISOString();
            }
            if (callForm.demo_installed && !selectedLead.demo_installed_date) {
                updates.demo_installed_date = new Date().toISOString();
            }
            await saveLead(updates);

            setSelectedLead(updates);
            setCallForm({ ...EMPTY_CALL, temperature: updates.temperature });
            setCallSeconds(0);
            fetchActivities();
        } catch (err) {
            alert("Could not log call: " + (err.message || "Unknown error"));
        } finally {
            setLogging(false);
        }
    };

    // ── Confirm deal ─────────────────────────────────────────────
    const canConfirm = selectedLead && !!selectedLead.demo_installed_date &&
        !["confirmed", "client", "lost"].includes(selectedLead.status);

    const onConfirmDeal = async () => {
        const amount = Number(confirmForm.amount);
        const installments = Math.max(1, parseInt(confirmForm.installments, 10) || 1);
        if (!amount || amount <= 0) { alert("Enter a valid deal amount."); return; }
        if (!confirmForm.firstDueDate) { alert("Enter the first payment due date."); return; }

        setConfirming(true);
        try {
            // 1. Create the client record
            const { data: newAgency, error: agencyErr } = await supabase
                .from("agencies")
                .insert({
                    agency_name: selectedLead.lead_name,
                    mobile: selectedLead.mobile_number,
                    place: selectedLead.place || "",
                    email_id: "", address1: "", address2: "", address3: "", phone: "",
                    software_key: "", gst_no: "", is_rate_inclusive: "",
                    is_mobile_approved: false, mobile_valid_upto: null,
                    is_ewb_approved: false, is_e_invoice_approved: false, is_gst_approved: false,
                    is_sync_approved: false, sync_valid_upto: null, amc_valid_upto: null,
                    pincode: "", client_nos: 0, amc_amt: amount, api_balance: 0, whatsapp_balance: 0,
                    installed_on: null, activated_on: null, last_updated: null, last_opened: null,
                    owner_id: null, partner_id: null,
                    lead_id: selectedLead.lead_id, is_confirmed: true,
                })
                .select()
                .single();
            if (agencyErr) throw agencyErr;

            // 2. Generate the payment schedule
            const base = Math.floor((amount / installments) * 100) / 100;
            const rows = Array.from({ length: installments }, (_, i) => {
                const due = new Date(confirmForm.firstDueDate);
                due.setMonth(due.getMonth() + i);
                const isLast = i === installments - 1;
                const amt = isLast ? Math.round((amount - base * (installments - 1)) * 100) / 100 : base;
                return {
                    lead_id: selectedLead.lead_id,
                    client_id: newAgency.agency_id,
                    installment_no: i + 1,
                    due_date: due.toISOString().slice(0, 10),
                    amount: amt,
                    is_paid: false,
                };
            });
            const { error: scheduleErr } = await supabase.from("crm_payment_schedule").insert(rows);
            if (scheduleErr) throw scheduleErr;

            // 3. Move the lead to Confirmed
            const updated = {
                ...selectedLead,
                status: "confirmed",
                confirmed_amount: amount,
                confirmed_date: new Date().toISOString(),
                client_id: newAgency.agency_id,
            };
            await saveLead(updated);

            setSelectedLead(updated);
            setShowConfirmForm(false);
            setConfirmForm({ amount: "", installments: 1, firstDueDate: "" });
            fetchSchedule();
            fetchLeads();
            alert(`Deal confirmed. "${selectedLead.lead_name}" has been added to your Client list.`);
        } catch (err) {
            alert("Could not confirm deal: " + (err.message || "Unknown error") +
                "\n\nCheck that the required columns on your 'agencies' table allow the values sent here.");
        } finally {
            setConfirming(false);
        }
    };

    const onMarkPaid = async (row) => {
        try {
            await saveScheduleRow({
                ...row,
                is_paid: true,
                paid_date: new Date().toISOString().slice(0, 10),
                paid_amount: row.amount,
            });
            const remaining = schedule.filter((r) => r.schedule_id !== row.schedule_id && !r.is_paid);
            if (remaining.length === 0 && selectedLead) {
                const updated = { ...selectedLead, status: "client" };
                await saveLead(updated);
                setSelectedLead(updated);
                fetchLeads();
            }
            fetchSchedule();
        } catch (err) {
            alert("Could not update payment: " + err.message);
        }
    };

    const visibleSoftware = softwareOptions.filter((s) =>
        (s.group_name ?? "").toLowerCase().includes(softwareSearch.trim().toLowerCase())
    );

    return (
        <div className="crm-wrap">
            <div className="crm-toolbar">
                <div className="crm-tabs">
                    {TabFilters.map((t) => (
                        <button key={t.key} className={`crm-tab${tab === t.key ? " active" : ""}`} onClick={() => setTab(t.key)}>
                            {t.label}
                        </button>
                    ))}
                </div>
                <div className="crm-toolbar-actions">
                    <div className="crm-search-wrapper">
                        <Search size={15} className="crm-search-icon" />
                        <input className="crm-search" placeholder="Search leads…" value={search}
                            onChange={(e) => setSearch(e.target.value)} />
                    </div>
                    <button className="crm-btn-icon" onClick={fetchLeads} title="Refresh"><RefreshCw size={16} /></button>
                    <button className="crm-btn-primary" onClick={openAdd}><Plus size={16} /> New Lead</button>
                </div>
            </div>

            <div className="crm-table-wrap">
                <table className="crm-table">
                    <thead>
                        <tr>
                            <th>Lead</th><th>Place</th><th>Mobile</th><th>Software</th>
                            <th>Status</th><th>Temp</th><th>Next Call</th><th>Assigned</th><th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="crm-empty">Loading…</td></tr>
                        ) : filteredLeads.length === 0 ? (
                            <tr><td colSpan={9} className="crm-empty">No leads found.</td></tr>
                        ) : filteredLeads.map((lead) => {
                            const TempIcon = TEMP_ICON[lead.temperature] || Sun;
                            return (
                                <tr key={lead.lead_id} className="crm-row" onClick={() => openLead(lead)}>
                                    <td><strong>{lead.lead_name}</strong><div className="crm-subtext">{lead.contact_person}</div></td>
                                    <td>{lead.place || "—"}</td>
                                    <td>{lead.mobile_number}</td>
                                    <td>{(lead.interested_software ?? []).map(softwareName).join(", ") || "—"}</td>
                                    <td><span className={`crm-badge crm-badge-${lead.status}`}>{STATUS_LABELS[lead.status] ?? lead.status}</span></td>
                                    <td><span className={`crm-temp crm-temp-${lead.temperature}`}><TempIcon size={14} /> {lead.temperature}</span></td>
                                    <td>{formatDate(lead.next_call_date)}</td>
                                    <td>{executiveName(lead.assigned_user_id)}</td>
                                    <td onClick={(e) => e.stopPropagation()}>
                                        <button className="crm-icon-btn danger" onClick={() => onDeleteLead(lead)} title="Delete">
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* ── Add Lead Modal ─────────────────────────────────── */}
            {showAddForm && (
                <div className="crm-modal-overlay">
                    <div className="crm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal-header">
                            <h3>New Lead</h3>
                            <button className="crm-icon-btn" onClick={closeAdd}><X size={18} /></button>
                        </div>
                        <div className="crm-modal-body">
                            <div className="crm-field-row">
                                <div className="crm-field">
                                    <label>Name *</label>
                                    <input value={form.lead_name} onChange={(e) => set("lead_name", e.target.value)} />
                                    {errors.lead_name && <span className="crm-error">{errors.lead_name}</span>}
                                </div>
                                <div className="crm-field">
                                    <label>Place</label>
                                    <input value={form.place} onChange={(e) => set("place", e.target.value)} />
                                </div>
                            </div>
                            <div className="crm-field-row">
                                <div className="crm-field">
                                    <label>Contact Person</label>
                                    <input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} />
                                </div>
                                <div className="crm-field">
                                    <label>Mobile Number *</label>
                                    <input value={form.mobile_number} onChange={(e) => set("mobile_number", e.target.value)} />
                                    {errors.mobile_number && <span className="crm-error">{errors.mobile_number}</span>}
                                </div>
                            </div>
                            <div className="crm-field-row">
                                <div className="crm-field">
                                    <label>Source</label>
                                    <select value={form.source} onChange={(e) => set("source", e.target.value)}>
                                        {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </select>
                                </div>
                                <div className="crm-field">
                                    <label>Assign To</label>
                                    <select value={form.assigned_user_id} onChange={(e) => set("assigned_user_id", e.target.value)}>
                                        <option value="">— Unassigned —</option>
                                        {executives.map((x) => <option key={x.executive_id} value={x.executive_id}>{x.executive_name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="crm-field">
                                <label>Interested Software</label>
                                <div className="crm-search-wrapper" style={{ marginBottom: 6 }}>
                                    <Search size={14} className="crm-search-icon" />
                                    <input className="crm-search" placeholder="Search software…" value={softwareSearch}
                                        onChange={(e) => setSoftwareSearch(e.target.value)} />
                                </div>
                                <div className="crm-multiselect">
                                    {visibleSoftware.length === 0 ? (
                                        <div className="crm-empty-inline">No matches</div>
                                    ) : visibleSoftware.map((s) => {
                                        const isSel = (form.interested_software ?? []).map(Number).includes(Number(s.group_id));
                                        return (
                                            <div key={s.group_id} className={`crm-option${isSel ? " selected" : ""}`}
                                                onClick={() => toggleSoftware(s.group_id)}>
                                                <span className="crm-check">{isSel ? "✓" : ""}</span>{s.group_name}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                        <div className="crm-modal-footer">
                            <button className="crm-btn-secondary" onClick={closeAdd}>Cancel</button>
                            <button className="crm-btn-primary" disabled={saving} onClick={onSaveLead}>
                                {saving ? "Saving…" : "Save Lead"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Lead Workspace Modal ───────────────────────────── */}
            {selectedLead && (
                <div className="crm-modal-overlay">
                    <div className="crm-modal crm-modal-wide" onClick={(e) => e.stopPropagation()}>
                        <div className="crm-modal-header">
                            <div>
                                <h3>{selectedLead.lead_name}</h3>
                                <div className="crm-subtext">{selectedLead.place} · {selectedLead.mobile_number}</div>
                            </div>
                            <button className="crm-icon-btn" onClick={closeLead}><X size={18} /></button>
                        </div>

                        <div className="crm-modal-body crm-workspace">
                            <div className="crm-workspace-main">
                                <div className="crm-status-row">
                                    <span className={`crm-badge crm-badge-${selectedLead.status}`}>
                                        {STATUS_LABELS[selectedLead.status] ?? selectedLead.status}
                                    </span>
                                    {selectedLead.demo_requested_date && <span className="crm-flag">Demo Requested {formatDate(selectedLead.demo_requested_date)}</span>}
                                    {selectedLead.demo_installed_date && <span className="crm-flag ok">Demo Installed {formatDate(selectedLead.demo_installed_date)}</span>}
                                    {canConfirm && (
                                        <button className="crm-btn-primary crm-confirm-btn" onClick={() => setShowConfirmForm((s) => !s)}>
                                            <CheckCircle2 size={15} /> Confirm Deal
                                        </button>
                                    )}
                                </div>

                                {showConfirmForm && (
                                    <div className="crm-confirm-form">
                                        <div className="crm-field-row">
                                            <div className="crm-field">
                                                <label>Deal Amount (₹)</label>
                                                <input type="number" value={confirmForm.amount}
                                                    onChange={(e) => setConfirmForm((p) => ({ ...p, amount: e.target.value }))} />
                                            </div>
                                            <div className="crm-field">
                                                <label>No. of Installments</label>
                                                <input type="number" min={1} value={confirmForm.installments}
                                                    onChange={(e) => setConfirmForm((p) => ({ ...p, installments: e.target.value }))} />
                                            </div>
                                            <div className="crm-field">
                                                <label>First Due Date</label>
                                                <input type="date" value={confirmForm.firstDueDate}
                                                    onChange={(e) => setConfirmForm((p) => ({ ...p, firstDueDate: e.target.value }))} />
                                            </div>
                                        </div>
                                        <button className="crm-btn-primary" disabled={confirming} onClick={onConfirmDeal}>
                                            {confirming ? "Confirming…" : "Confirm & Create Client"}
                                        </button>
                                    </div>
                                )}

                                {/* Log a call */}
                                <div className="crm-log-call">
                                    <div className="crm-log-call-header">
                                        <h4>Log a Call</h4>
                                        <div className="crm-timer">
                                            <Clock size={14} /> {formatDuration(callSeconds)}
                                            {!timerRunning ? (
                                                <button className="crm-btn-secondary sm" onClick={() => setTimerRunning(true)}>
                                                    <PhoneCall size={13} /> Start
                                                </button>
                                            ) : (
                                                <button className="crm-btn-secondary sm" onClick={() => setTimerRunning(false)}>
                                                    <Phone size={13} /> Stop
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div className="crm-field-row">
                                        <div className="crm-field">
                                            <label>Outcome</label>
                                            <select value={callForm.outcome} onChange={(e) => setCallForm((p) => ({ ...p, outcome: e.target.value }))}>
                                                {Object.entries(OUTCOME_LABELS).filter(([k]) => k !== "payment_reminder" && k !== "service").map(([k, v]) => (
                                                    <option key={k} value={k}>{v}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="crm-field">
                                            <label>Next Call Date</label>
                                            <input type="date" value={callForm.next_call_date}
                                                onChange={(e) => setCallForm((p) => ({ ...p, next_call_date: e.target.value }))} />
                                        </div>
                                    </div>

                                    <div className="crm-field">
                                        <label>Temperature</label>
                                        <div className="crm-temp-picker">
                                            {["hot", "warm", "cold"].map((t) => {
                                                const Icon = TEMP_ICON[t];
                                                return (
                                                    <button key={t} type="button"
                                                        className={`crm-temp-choice crm-temp-${t}${callForm.temperature === t ? " selected" : ""}`}
                                                        onClick={() => setCallForm((p) => ({ ...p, temperature: t }))}>
                                                        <Icon size={14} /> {t}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="crm-field-row">
                                        <label className="crm-checkbox">
                                            <input type="checkbox" checked={callForm.demo_requested}
                                                onChange={(e) => setCallForm((p) => ({ ...p, demo_requested: e.target.checked }))} />
                                            Requested for Demo
                                        </label>
                                        <label className="crm-checkbox">
                                            <input type="checkbox" checked={callForm.demo_installed}
                                                onChange={(e) => setCallForm((p) => ({ ...p, demo_installed: e.target.checked }))} />
                                            Demo Installed
                                        </label>
                                    </div>

                                    <div className="crm-field">
                                        <label>Notes</label>
                                        <textarea rows={3} value={callForm.notes}
                                            onChange={(e) => setCallForm((p) => ({ ...p, notes: e.target.value }))} />
                                    </div>

                                    <button className="crm-btn-primary" disabled={logging} onClick={onLogCall}>
                                        {logging ? "Saving…" : "Save Call Log"}
                                    </button>
                                </div>

                                {schedule.length > 0 && (
                                    <div className="crm-schedule">
                                        <h4><IndianRupee size={14} /> Payment Schedule</h4>
                                        <table className="crm-table sm">
                                            <thead><tr><th>#</th><th>Due Date</th><th>Amount</th><th>Status</th><th></th></tr></thead>
                                            <tbody>
                                                {schedule.map((row) => (
                                                    <tr key={row.schedule_id}>
                                                        <td>{row.installment_no}</td>
                                                        <td>{formatDate(row.due_date)}</td>
                                                        <td>₹{row.amount}</td>
                                                        <td>{row.is_paid ? <span className="crm-flag ok">Paid {formatDate(row.paid_date)}</span> : <span className="crm-flag">Pending</span>}</td>
                                                        <td>{!row.is_paid && <button className="crm-btn-secondary sm" onClick={() => onMarkPaid(row)}>Mark Paid</button>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="crm-workspace-timeline">
                                <h4>Call History</h4>
                                {activities.length === 0 ? (
                                    <div className="crm-empty-inline">No calls logged yet.</div>
                                ) : activities.slice().reverse().map((a) => (
                                    <div key={a.activity_id} className="crm-timeline-item">
                                        <div className="crm-timeline-date">{formatDateTime(a.activity_date)}</div>
                                        <div className="crm-timeline-outcome">{OUTCOME_LABELS[a.outcome] ?? a.outcome}</div>
                                        {a.notes && <div className="crm-timeline-notes">{a.notes}</div>}
                                        <div className="crm-timeline-meta">
                                            {executiveName(a.user_id)} · {formatDuration(a.duration_seconds)}
                                            {a.next_call_date && <> · Next: {formatDate(a.next_call_date)}</>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CrmLeads;